/**
 * @module api/api
 * @description Axios 인스턴스 및 공통 API 유틸 함수 모음.
 *   - CS 백엔드(/api/cs)와 결제 서버(/api/payment) 두 가지 인스턴스를 관리합니다.
 *   - 인증 방식(세션/바이패스/미인증)에 따른 전용 호출 함수를 제공합니다.
 */
import axios from 'axios'
import router from '@/router/index'
import i18n from '@/locales/i18n'
import {
  setupInterceptors,
  notifyMessage,
  getErrorStatus,
  getErrorData,
  notifyErrorCodeOrDefault,
  errorCodeMessage,
  handleSessionUnauthorized,
  handleBypassUnauthorized,
  refreshToken as _refreshToken
} from '@/api/interceptors'

axios.defaults.withCredentials = true

// CS 백엔드용 Axios 인스턴스 (인터셉터 적용)
const axiosInstance = axios.create({
  baseURL: '/api/cs'
})

// 결제 서버용 Axios 인스턴스 (인증 불필요)
const axiosInstancePayment = axios.create({
  baseURL: '/api/payment'
})

setupInterceptors(axiosInstance, router)

const { t } = i18n.global

// Re-export refreshToken for backward compatibility
export const refreshToken = () => _refreshToken(axiosInstance)

// ============================================================
// 인증 없는 API (공개 엔드포인트, SMS 인증 등)
// ============================================================

/**
 * 인증 없이 CS 백엔드를 호출합니다.
 * 로그인 전 공개 API, SMS 인증 등에 사용됩니다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드 (get, post 등)
 * @param {Object} [data] - 요청 바디
 * @param {Object} [config] - 추가 axios 옵션(예: timeout)
 * @returns {Promise<any>} 응답 데이터 또는 에러 객체
 */
export const noAuthApi = async (url, method, data, config = {}) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      ...config,
      _skipAuth: true,
      _skipErrorHandler: true
    })
    return response.data
  } catch (e) {
    window.__APP_LOG__(e)
    return e
  }
}

/**
 * 인증 없이 결제 서버를 호출합니다.
 * PortOne 결제 연동에 사용됩니다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드
 * @param {Object} [data] - 요청 바디
 * @returns {Promise<AxiosResponse|Error>} Axios 응답 객체 또는 에러
 */
export const noAuthPaymentApi = async (url, method, data) => {
  try {
    const response = await axiosInstancePayment({
      url: url,
      method: method,
      data: data,
      withCredentials: false
    })
    return response
  } catch (e) {
    window.__APP_LOG__('Payment API error:', e)
    return e
  }
}

// ============================================================
// 로그인 전용 API
// ============================================================

/**
 * 로그인 API를 호출합니다.
 * 400 에러 시 에러 코드 기반 토스트를 표시하고 에러 데이터를 반환합니다.
 *
 * @param {string} url - 로그인 엔드포인트 URL
 * @param {string} method - HTTP 메서드
 * @param {Object} data - 로그인 정보 (loginId, password 등)
 * @returns {Promise<{status: number, data: any}|undefined>} 응답 또는 undefined
 */
export const loginApi = async (url, method, data) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      _skipAuth: true,
      _skipErrorHandler: true
    })
    return {
      status: response.status,
      data: response.data
    }
  } catch (e) {
    const status = getErrorStatus(e)
    if (status === 400) {
      notifyErrorCodeOrDefault(e, 'message.loginFail')
      return getErrorData(e)
    }
    notifyMessage(t('message.loginFail'))
  }
}

// ============================================================
// 표준 CRUD API (인증 포함)
// ============================================================

/**
 * 인증된 GET 요청을 수행합니다.
 * 실패 시 false를 반환합니다 (에러는 인터셉터가 처리).
 *
 * @param {string} url - 요청 URL (쿼리스트링 포함 가능)
 * @returns {Promise<any|false>} 응답 데이터 또는 false
 */
export const getApi = async (url) => {
  try {
    const response = await axiosInstance.get(url, {
      headers: { 'Content-Type': 'application/json;charset=UTF-8' }
    })
    return response.data
  } catch {
    return false
  }
}

/**
 * 인증된 GET 요청을 수행하되, 실패 시 삼키지 않고 예외를 그대로 던집니다.
 * getApi 는 실패 시 false 로 반환해 호출자가 상태코드/바디로 분기할 수 없으므로,
 * 그런 분기가 필요한 호출부(예: 401/403 판별)만 이 헬퍼를 사용합니다.
 *
 * @param {string} url - 요청 URL
 * @param {Object} [config={}] - Axios 요청 설정(추가 헤더 등)
 * @returns {Promise<AxiosResponse>} Axios 응답 객체
 * @throws {Error} 요청 실패 시 Axios 에러를 그대로 던집니다.
 */
export const getApiRaw = (url, config = {}) => axiosInstance.get(url, { ...config, _skipErrorHandler: true })

/**
 * 저장/수정 API를 호출합니다. (POST, PUT, PATCH)
 * 성공 시 성공 토스트를 표시하고 응답을 반환합니다.
 * 쓰기 요청 전 토큰을 자동 갱신합니다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드 (post, put, patch)
 * @param {Object} [data] - 요청 바디
 * @param {Object} [options] - 호출 옵션
 * @param {boolean} [options.returnErrorResponse=false] - true면 실패 시 false 대신 {status, data}를 반환합니다.
 *   에러 토스트는 인터셉터가 이미 표시하므로, 호출부가 에러 코드로 추가 분기해야 할 때만 사용합니다. (SURINP-480)
 * @param {string[]} [options.silentErrorCodes] - 400 응답 code 가 이 목록에 있으면 인터셉터가 토스트를 띄우지 않습니다.
 *   호출부가 그 코드를 받아 스스로 재시도·안내할 때만 사용합니다(returnErrorResponse 와 함께). (SURINP-893)
 * @param {boolean} [options.silentSuccess=false] - true면 200 성공 토스트(「저장되었습니다」)를 띄우지 않습니다.
 *   호출부가 성공 안내를 직접 띄울 때만 사용합니다. 실패 안내는 그대로입니다. (SURINP-913)
 * @returns {Promise<AxiosResponse|{status: number, data: any}|false>} 응답 또는 false
 */
export const api = async (url, method, data, options = {}) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      _autoRefresh: true,
      _silentErrorCodes: options.silentErrorCodes
    })

    if (response.status === 200) {
      if (!options.silentSuccess) notifyMessage(t('message.saved200'), 'success')
      return response
    } else if (response.status === 400) {
      notifyMessage(errorCodeMessage(response.data.code, 'message.saved400'))
      return false
    } else if (response.status === 500) {
      notifyMessage(t('message.saved400'))
      return false
    }
    return response
  } catch (e) {
    // SURINP-480: 기본 동작은 기존과 동일하게 false.
    // opt-in 한 호출부만 에러 코드로 분기할 수 있도록 상태/바디를 노출한다.
    if (options.returnErrorResponse) {
      return { status: getErrorStatus(e), data: getErrorData(e) }
    }
    return false
  }
}

/**
 * 삭제 API를 호출합니다.
 * 성공 시 삭제 성공 토스트를 표시합니다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드 (delete)
 * @param {Object} [data] - 요청 바디 (일괄 삭제 등에 사용)
 * @returns {Promise<AxiosResponse|false>} 응답 또는 false
 */
export const deleteApi = async (url, method, data) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      _autoRefresh: true
    })

    if (response.status === 200) {
      notifyMessage(t('message.deleted200'), 'success')
      return response
    } else if (response.status === 400) {
      notifyMessage(errorCodeMessage(response.data.code, 'message.deleted400'))
      return false
    } else if (response.status === 500) {
      notifyMessage(t('message.deleted400'))
      return false
    }
    return response
  } catch {
    return false
  }
}

/**
 * 커스텀 에러 처리가 필요한 API를 호출합니다.
 * 인터셉터의 자동 에러 핸들링을 건너뛰고, 호출자가 상태코드를 직접 처리합니다.
 * 401 은 세션 만료 처리, 403 은 권한 없음 안내를 내부에서 수행합니다.
 *
 * 반환 계약(SURINP-686):
 *   - 성공·실패 모두 {status, data} 를 돌려주며 false 를 반환하지 않는다.
 *   - 성공 판정은 호출부가 status === 200 으로 한다.
 *   - 네트워크 오류(응답 없음)는 status 가 null 이다.
 *   - 401(세션 만료)·403(권한 없음)은 내부에서 안내하므로 호출부는 중복 안내하지 않는다.
 *   - 인터셉터 공통 토스트는 꺼져 있으므로(_skipErrorHandler) 그 밖의 실패는 호출부가 직접 안내한다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드
 * @param {Object} [data] - 요청 바디
 * @returns {Promise<{status: (number|null), data: any}>} 상태코드와 데이터
 */
export const noCustomApi = async (url, method, data) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      _skipErrorHandler: true
    })
    return {
      status: response.status,
      data: response.data
    }
  } catch (e) {
    handleSessionUnauthorized(e)
    // 403 은 세션 만료가 아니라 권한 없음 — 로그아웃 없이 안내만 한다(SURINP-738).
    // 단, 토큰 갱신 요청의 403 은 갱신 불가 = 세션 만료라 기존처럼 세션 처리한다.
    if (getErrorStatus(e) === 403) {
      if (String(url).includes('/token/refresh')) handleSessionUnauthorized({ response: { status: 401 } })
      else notifyMessage(t('message.forbidden'))
    }
    return {
      status: getErrorStatus(e),
      data: getErrorData(e)
    }
  }
}

// ============================================================
// 파일 다운로드/업로드 API
// ============================================================

/**
 * 엑셀 파일을 다운로드합니다.
 * Blob으로 응답을 받아 브라우저 다운로드를 트리거합니다.
 *
 * @param {string} queryString - 다운로드 URL (쿼리스트링 포함)
 * @param {string} fileName - 저장할 파일명 (.xlsx 확장자 자동 추가)
 * @returns {Promise<void>}
 */
export const excelApi = async (queryString, fileName) => {
  try {
    const response = await axiosInstance.get(queryString, {
      responseType: 'blob',
      _skipErrorHandler: true
    })

    const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${fileName}.xlsx`)
    document.body.appendChild(link)
    link.click()
  } catch (error) {
    window.__APP_LOG__(error)
  }
}

/**
 * multipart/form-data 형식으로 파일과 데이터를 함께 전송합니다.
 * 이미지, 첨부파일이 포함된 저장/수정 요청에 사용됩니다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드 (post, put)
 * @param {FormData} data - FormData 객체
 * @returns {Promise<AxiosResponse|false>} 응답 또는 false
 */
export const multipartFormDataApi = async (url, method, data) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    if (response.status === 200) {
      notifyMessage(t('message.saved200'), 'success')
      return response
    } else if (response.status === 400) {
      notifyMessage(errorCodeMessage(response.data.code, 'message.saved400'))
      return false
    } else if (response.status === 500) {
      notifyMessage(t('message.saved400'))
      return false
    }
    return response
  } catch {
    return false
  }
}

/**
 * 이미지를 Blob 형태로 다운로드합니다.
 * 뷰어에서 미리보기 또는 다운로드 용도로 사용됩니다.
 *
 * @param {string} url - 이미지 다운로드 URL
 * @returns {Promise<AxiosResponse>} Blob 데이터가 포함된 응답
 */
export const imageDownloadApi = async (url) => {
  return await axiosInstance.get(url, {
    responseType: 'blob',
    _skipErrorHandler: true
  })
}

/**
 * 파일 업로드(엑셀 등)를 수행합니다.
 * 에러 핸들링을 건너뛰고 응답/에러를 그대로 반환합니다.
 * 호출자가 직접 상태코드와 결과를 처리해야 합니다.
 *
 * @param {string} url - 업로드 URL
 * @param {string} method - HTTP 메서드 (post)
 * @param {FormData} data - 업로드할 파일이 포함된 FormData
 * @returns {Promise<AxiosResponse|Error>} 응답 또는 에러
 */
export const fileApi = async (url, method, data) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      headers: { 'Content-Type': 'multipart/form-data' },
      _skipErrorHandler: true
    })
    return response
  } catch (e) {
    return e
  }
}

// ============================================================
// 바이패스 API (X-Bypass-Token 기반 인증)
// ============================================================

/**
 * X-Bypass-Token을 사용하여 GET 요청을 수행합니다.
 * 수리기사/고객 모바일 화면의 토큰 기반 인증에 사용됩니다.
 *
 * @param {string} url - 요청 URL
 * @returns {Promise<any|false>} 응답 데이터 또는 false
 */
export const bypassGetApi = async (url) => {
  try {
    const response = await axiosInstance.get(url, {
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      _useBypass: true
    })
    return response.data
  } catch {
    return false
  }
}

/**
 * X-Bypass-Token을 사용하여 쓰기 요청을 수행합니다.
 * 401/403 발생 시 바이패스 전용 만료 처리를 수행합니다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드 (post, put 등)
 * @param {Object} [data] - 요청 바디
 * @returns {Promise<{status: number, data: any}>} 상태코드와 데이터
 */
export const bypassApi = async (url, method, data) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      _useBypass: true,
      _skipErrorHandler: true
    })
    return {
      status: response.status,
      data: response.data
    }
  } catch (e) {
    handleBypassUnauthorized(e)
    return {
      status: getErrorStatus(e),
      data: getErrorData(e)
    }
  }
}

// ============================================================
// 공개 진행 조회 API (SURINP-733 — 고정 X-Bypass-Token 없음)
// 서버가 고정 헤더를 면제한 /bypass/customer/auth/sms/** · /bypass/repair-request/customer/** 전용
// ============================================================

/**
 * 고객 공개 진행 조회 요청을 수행합니다. 고정 X-Bypass-Token·Authorization 을 싣지 않고,
 * searchToken 이 있으면 X-Customer-Search-Token 헤더로 보냅니다.
 * 토스트를 띄우지 않으므로 호출자가 상태 코드(401/403/429)별 안내를 맡습니다.
 *
 * @param {string} url - 요청 URL
 * @param {string} method - HTTP 메서드 (get, post 등)
 * @param {Object} [data] - 요청 바디
 * @param {string} [searchToken] - SMS 인증 성공 시 받은 진행 조회 토큰
 * @returns {Promise<{status: number|null, data: any}>} 상태코드와 데이터
 */
export const customerSearchApi = async (url, method, data, searchToken) => {
  try {
    const response = await axiosInstance({
      url,
      method,
      data,
      _customerSearch: true,
      _searchToken: searchToken,
      _skipErrorHandler: true
    })
    return {
      status: response.status,
      data: response.data
    }
  } catch (e) {
    return {
      status: getErrorStatus(e),
      data: getErrorData(e)
    }
  }
}

/**
 * 고객 공개 조회 GET 요청을 수행합니다(검색 토큰 없음 — 시리얼 배송 조회용).
 * bypassGetApi 와 같은 계약이지만 고정 X-Bypass-Token 을 싣지 않습니다.
 *
 * @param {string} url - 요청 URL
 * @returns {Promise<any|false>} 응답 데이터 또는 false
 */
export const customerSearchGetApi = async (url) => {
  try {
    const response = await axiosInstance.get(url, {
      headers: { 'Content-Type': 'application/json;charset=UTF-8' },
      _customerSearch: true
    })
    return response.data
  } catch {
    return false
  }
}
